import { useRef, useState } from "react";
import { heroFocusImages } from "@/mocks/techData";

const focusTabs = [
  { id: "mobile", label: "Mobile" },
  { id: "laptop", label: "Laptop" },
  { id: "accessories", label: "Accessories" },
];

export default function Hero() {
  const [focus, setFocus] = useState("mobile");
  const frameRef = useRef<HTMLDivElement>(null);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });

  const onMove = (e: React.MouseEvent) => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nx = (e.clientX - rect.left) / rect.width - 0.5;
    const ny = (e.clientY - rect.top) / rect.height - 0.5;
    setParallax({ x: nx, y: ny });
  };

  return (
    <section
      id="top"
      ref={frameRef}
      onMouseMove={onMove}
      className="relative flex min-h-[100svh] w-full items-center overflow-hidden bg-foreground-950"
    >
      {/* Crossfading background layers */}
      {focusTabs.map((t) => (
        <div
          key={t.id}
          className="absolute inset-0 transition-opacity duration-[1200ms] ease-out"
          style={{ opacity: focus === t.id ? 1 : 0 }}
        >
          <img
            src={heroFocusImages[t.id]}
            alt={t.label === "Mobile" ? "Premium smartphone at Fist Limited in Liverpool" : t.label === "Laptop" ? "Slim laptop at Fist Limited in Liverpool" : "Premium technology accessories at Fist Limited in Liverpool"}
            className="h-full w-full object-cover object-center"
          />
        </div>
      ))}

      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/45 to-foreground-950/90"></div>

      {/* Subtle parallax glow */}
      <div
        className="pointer-events-none absolute inset-0 transition-transform duration-300 ease-out"
        style={{ transform: `translate(${parallax.x * -18}px, ${parallax.y * -14}px)` }}
      >
        <div className="absolute left-1/2 top-1/2 h-[60%] w-[60%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-500/10 blur-3xl"></div>
      </div>

      <div
        className="container-x relative z-10 w-full pt-28 pb-24 md:pt-32"
        style={{ transform: `translate(${parallax.x * 10}px, ${parallax.y * 8}px)` }}
      >
        <p className="eyebrow-light mb-5 flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-primary-500"></span>
          Technology Retail · Liverpool
        </p>

        <h1 className="font-heading text-4xl leading-[0.98] tracking-tight text-background-50 sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl">
          TECHNOLOGY,
          <br />
          <span className="text-primary-400">YOUR WAY.</span>
        </h1>

        <p className="mt-5 max-w-xl text-sm text-background-100/75 sm:text-base md:text-lg">
          Mobiles, laptops and everyday technology essentials in Liverpool — sourced, checked and
          ready for your day.
        </p>

        <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
          <a href="#products" className="btn-primary h-11 px-5 text-xs sm:h-12 sm:px-7 sm:text-sm">
            Explore Technology
            <i className="ri-arrow-right-line"></i>
          </a>
          <a href="#visit" className="btn-light-outline h-11 px-5 text-xs sm:h-12 sm:px-7 sm:text-sm">
            Visit Fist Limited
          </a>
        </div>

        {/* Category focus switcher */}
        <div className="mt-8 flex w-full items-center rounded-full border border-background-50/15 bg-background-50/5 p-1 backdrop-blur-sm sm:mt-10 sm:w-fit sm:p-1.5">
          {focusTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setFocus(t.id)}
              className={`flex-1 cursor-pointer rounded-full px-3 py-2 font-label text-[10px] transition-all duration-300 sm:px-5 sm:py-2.5 sm:text-xs ${
                focus === t.id
                  ? "bg-primary-500 text-foreground-950"
                  : "text-background-100/80 hover:text-background-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Scroll cue */}
      <a
        href="#world"
        aria-label="Scroll down"
        className="absolute bottom-16 left-1/2 z-10 -translate-x-1/2 text-background-50/60 transition-colors hover:text-primary-400 sm:bottom-6"
      >
        <i className="ri-arrow-down-line animate-bounce text-xl"></i>
      </a>
    </section>
  );
}